// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { Match, Redacted } from '../types';

/**
 * Masking, and the structural invariant it enforces:
 *
 *     A FULL SECRET VALUE NEVER ENTERS ANY OBJECT REACHABLE FROM AN ARTEFACT BUILDER.
 *
 * Masking happens at MATCH-CONSTRUCTION time, not at render time. `Match` has no `value` field at
 * all, so there is no "remember to mask this" step for a future edit to forget. This module is the
 * only one that mints `Redacted<T>`; every renderer and artefact builder accepts nothing else, and
 * tsc --strict rejects a plain `Match[]` where `Redacted<Match>[]` is required (TS2345).
 *
 * THE SPEC SAID "mask to the last four characters". That rule is wrong for three shapes and one
 * length:
 *   - last-4 of a `DATABASE_URL` is `5432` or the database name — useless, and a PREFIX would leak
 *     the host, so the URL case renders the scheme and nothing else;
 *   - last-4 of a PEM block is `KEY-`;
 *   - for a 9-character secret, 4 characters is 44% of it.
 *
 * There is NO REVEAL CONTROL ANYWHERE IN THIS PRODUCT. The user already has the .env.
 */

const BULLET = '••••';

/** How much of a value may ever be shown: never a prefix, never more than a quarter, never over 4. */
export function revealCount(length: number): number {
  if (length < 12) return 0;
  return Math.min(4, Math.floor(length / 4));
}

export interface JwtSummary {
  readonly segments: number;
  readonly role?: string;
  readonly ref?: string;
  readonly iss?: string;
  readonly expiredOn?: string;
  readonly bypassRls?: boolean;
}

/**
 * The mask ladder — first match wins, suffix only, never a prefix.
 *
 * Applied unconditionally, INCLUDING to values classified public-by-design. A uniform mask is what
 * stops a future refactor printing the "safe" ones in full and, one condition inversion later, the
 * unsafe ones.
 */
export function maskValue(value: string, jwt?: JwtSummary): string {
  const n = value.length;

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    const scheme = value.slice(0, value.indexOf('://') + 3);
    return `${BULLET} (${scheme} URL, ${n} chars)`;
  }
  if (jwt) {
    const bits = [`${BULLET} (JWT, ${jwt.segments} segments, ${n} chars)`];
    if (jwt.role) bits.push(`role=${jwt.role}`);
    if (jwt.ref) bits.push(`ref=${jwt.ref}`);
    if (jwt.bypassRls) bits.push('⚠ BYPASSRLS');
    if (jwt.expiredOn) bits.push(`expired ${jwt.expiredOn}`);
    return bits.join(' ');
  }
  if (value.startsWith('-----BEGIN')) {
    const m = /^-----BEGIN ([A-Z ]+)-----/.exec(value);
    return `${BULLET} (PEM ${m?.[1]?.trim() ?? 'BLOCK'}, ${n} chars)`;
  }
  const reveal = revealCount(n);
  if (reveal === 0) return `${BULLET} (${n} chars)`;
  return `${BULLET}…${value.slice(n - reveal)} (${n} chars)`;
}

/**
 * Elide unbroken high-entropy runs in a context window.
 *
 * Channel 3 of the leak-channel table: a secret NOT in the user's .env can sit inside the ±30
 * characters we render around a match, and no needle exists for it, so nothing else would catch it.
 *
 * TWO TUNING DECISIONS, both about not destroying the display:
 *
 * 1. `.` and `-` are EXCLUDED from the character class. With them included,
 *    `window.__vitePreload.someVeryLongPropertyChain` elides as one 46-character run and minified
 *    context becomes pure elision — the display stops being useful and the user stops trusting it.
 *
 * 2. Length alone is still not enough, because a single camelCase identifier can be 25 characters.
 *    A run also has to LOOK random. Two signals, in order:
 *      (a) it carries a digit or one of `+/=_`. Measured: only 0.696% of random 24-character
 *          base64 runs contain none, so this alone catches 99.3% of them, and an identifier
 *          essentially never trips it.
 *      (b) failing that, its case flips often enough to be non-word-like. Measured over 2,000
 *          random all-alphabetic 28-character runs: median flip density 0.500, 5th percentile
 *          0.321. Measured over real camelCase identifiers: 0.258 to 0.367. The threshold is
 *          0.42 — above every camelCase sample, below the bulk of the random distribution.
 *
 * THE HONEST COST: an all-lowercase, digit-free 24-character secret survives this filter. Combining
 * the two measurements, that is roughly 0.7% x 25% of unknown blobs. This is DEFENCE IN DEPTH for
 * values leakmap has no needle for — every KNOWN value is masked at match-construction time and does
 * not depend on this heuristic at all.
 */
const RUN_RE = /[A-Za-z0-9+/=_]{24,}/g;
const FLIP_THRESHOLD = 0.42;

function looksRandom(run: string): boolean {
  if (/[0-9+/=_]/.test(run)) return true;
  let flips = 0;
  for (let i = 1; i < run.length; i++) {
    const a = run[i - 1]!;
    const b = run[i]!;
    if ((a >= 'a' && a <= 'z') !== (b >= 'a' && b <= 'z')) flips++;
  }
  return flips / run.length >= FLIP_THRESHOLD;
}

export function elideHighEntropy(s: string): string {
  return s.replace(RUN_RE, (m) => (looksRandom(m) ? `⟨${m.length} chars elided⟩` : m));
}

const CONTEXT = 30;

export interface ContextInput {
  /** The decoded text of the region around the match. */
  readonly text: string;
  /** Index within `text` at which the match begins. */
  readonly matchStart: number;
  readonly matchLength: number;
  /** Other known secret values that might overlap the window, with their masks. */
  readonly otherSecrets: readonly { value: string; masked: string }[];
}

/**
 * Build the two context fields.
 *
 * `before` and `after` are SEPARATE FIELDS and are never joined in the model, so no code path can
 * reconstitute `before + value + after`. The window is sliced on the match boundaries, so the value
 * itself is excluded by construction rather than by a substitution that could be got wrong.
 */
export function buildContext(input: ContextInput): { beforeRedacted: string; afterRedacted: string } {
  const { text, matchStart, matchLength, otherSecrets } = input;
  let before = text.slice(Math.max(0, matchStart - CONTEXT), matchStart);
  let after = text.slice(matchStart + matchLength, matchStart + matchLength + CONTEXT);

  // Channel 2: a NEIGHBOURING .env secret sitting in the window, possibly only half inside it.
  // Replace the whole value where it fits, and any partial overlap of at least 8 characters.
  for (const other of otherSecrets) {
    if (other.value.length < 8) continue;
    before = scrubValueAndClippedEnds(before, other.value, other.masked);
    after = scrubValueAndClippedEnds(after, other.value, other.masked);
  }

  return { beforeRedacted: elideHighEntropy(before), afterRedacted: elideHighEntropy(after) };
}

/**
 * Replace `value` in `hay`, and also any part of `value` that the window boundary cut in half — a
 * half-visible secret is still a visible secret.
 *
 * BOTH DIRECTIONS ARE CHECKED ON BOTH FIELDS. It is tempting to reason that the `before` field can
 * only ever be clipped one way, and that reasoning is wrong: `before` is the text PRECEDING the
 * match, so a neighbouring secret that starts before the window and runs into it leaves its own
 * TAIL at the START of `before`. An earlier version of this function had the two directions
 * inverted and left 28 characters of the neighbour in the output; a test now pins the case.
 */
function scrubValueAndClippedEnds(hay: string, value: string, masked: string): string {
  if (hay.includes(value)) return hay.split(value).join(masked);
  const min = 8;
  const max = Math.min(hay.length, value.length - 1);

  // `hay` starts with a suffix of `value` (the value was cut by the window's left edge).
  for (let len = max; len >= min; len--) {
    if (hay.startsWith(value.slice(value.length - len))) return masked + hay.slice(len);
  }
  // `hay` ends with a prefix of `value` (the value is cut by the window's right edge).
  for (let len = max; len >= min; len--) {
    if (hay.endsWith(value.slice(0, len))) return hay.slice(0, hay.length - len) + masked;
  }
  return hay;
}

/**
 * The single mint point. Everything downstream takes `Redacted<Match>` and cannot be handed a raw
 * one — that is a compile error, not a review comment.
 */
export function seal(m: Match): Redacted<Match> {
  return m as Redacted<Match>;
}

/**
 * The needle store. Raw values live here and nowhere else: module-private, no `toJSON`, and never a
 * property of anything passed to a renderer.
 *
 * Channel 18 of the leak-channel table is the reason for the shape: the Aho–Corasick trie CONTAINS
 * the patterns, so `JSON.stringify(someStateObject)` that happened to reach it would dump every
 * secret. Keeping the values behind a class with no serialiser and no public enumeration is what
 * stops that being one careless spread away.
 */
export class NeedleStore {
  private readonly values = new Map<string, string>();

  set(id: string, value: string): void {
    this.values.set(id, value);
  }

  /** Masked form only. There is no getter that returns the raw value to another module. */
  maskedFor(id: string, jwt?: JwtSummary): string {
    const v = this.values.get(id);
    return v === undefined ? `${BULLET} (unknown)` : maskValue(v, jwt);
  }

  /** For context scrubbing only, inside this module's own call graph. */
  pairsForScrubbing(): { value: string; masked: string }[] {
    return [...this.values.entries()].map(([id, value]) => ({ value, masked: this.maskedFor(id) }));
  }

  get size(): number {
    return this.values.size;
  }

  /** Deliberately defeats `JSON.stringify` — see channel 18. */
  toJSON(): string {
    return '[NeedleStore redacted]';
  }
}
